import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import { getDatabase, ref, push, set, get, remove, onValue, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { bakeImageOrientation } from './imageOrientation';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);
const storage = getStorage(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// --- Authentication ---

export function subscribeToAuth(callback) {
  return onAuthStateChanged(auth, callback);
}

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

export async function signInWithEmail(email, password) {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function signUpWithEmail(email, password, displayName) {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(result.user, { displayName });
  }
  return result.user;
}

export async function sendPasswordReset(email) {
  await sendPasswordResetEmail(auth, email);
}

export async function checkIsAdmin(uid) {
  const snapshot = await get(ref(database, `admins/${uid}`));
  return snapshot.val() === true;
}

export async function logOut() {
  await signOut(auth);
}

// --- Sessions (scoped per user) ---

function sessionPath(uid, sessionId) {
  return `users/${uid}/sessions/${sessionId}`;
}

export async function createSession(uid) {
  const sessionsRef = ref(database, `users/${uid}/sessions`);
  const newSessionRef = push(sessionsRef);
  const session = {
    id: newSessionRef.key,
    createdAt: Date.now(),
    title: null,
    document: null,
  };
  await set(newSessionRef, session);
  return session;
}

export async function deleteSession(uid, sessionId) {
  const sessionRef = ref(database, sessionPath(uid, sessionId));
  await remove(sessionRef);
}

export async function updateSessionTitle(uid, sessionId, title) {
  const titleRef = ref(database, `${sessionPath(uid, sessionId)}/title`);
  await set(titleRef, title);
}

export async function saveMessage(uid, sessionId, message) {
  const messagesRef = ref(database, `${sessionPath(uid, sessionId)}/messages`);
  const newMessageRef = push(messagesRef);
  await set(newMessageRef, {
    ...message,
    timestamp: Date.now(),
  });
}

export async function updateMessage(uid, sessionId, messageKey, updates) {
  const messageRef = ref(database, `${sessionPath(uid, sessionId)}/messages/${messageKey}`);
  await update(messageRef, updates);
}

export async function saveDocument(uid, sessionId, documentContent) {
  const docRef = ref(database, `${sessionPath(uid, sessionId)}/document`);
  await set(docRef, {
    content: documentContent,
    updatedAt: Date.now(),
  });
}

// --- Todo List ---

export async function saveTodoList(uid, sessionId, todoList) {
  const todoRef = ref(database, `${sessionPath(uid, sessionId)}/todoList`);
  await set(todoRef, {
    items: todoList,
    updatedAt: Date.now(),
  });
}

// --- Space Context Store ---

export async function saveSpaceContext(uid, sessionId, spaceContext) {
  const ctxRef = ref(database, `${sessionPath(uid, sessionId)}/spaceContext`);
  await set(ctxRef, {
    data: spaceContext,
    updatedAt: Date.now(),
  });
}

// --- Work Items ---

/**
 * Recursively strip undefined values from an object/array so Firebase set() won't reject it.
 */
function stripUndefined(value) {
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(stripUndefined);
  const cleaned = {};
  for (const [k, v] of Object.entries(value)) {
    if (v !== undefined) {
      cleaned[k] = stripUndefined(v);
    }
  }
  return cleaned;
}

export async function saveWorkItems(uid, sessionId, workItems) {
  const workItemsRef = ref(database, `${sessionPath(uid, sessionId)}/workItems`);
  await set(workItemsRef, {
    items: stripUndefined(workItems),
    updatedAt: Date.now(),
  });
}

// --- Space Analysis ---

export async function saveSpaceAnalysis(uid, sessionId, spaceAnalysis) {
  const analysisRef = ref(database, `${sessionPath(uid, sessionId)}/spaceAnalysis`);
  await set(analysisRef, {
    items: spaceAnalysis,
    updatedAt: Date.now(),
  });
}

// --- Decision Items (Open Items List) ---

export async function saveDecisionItems(uid, sessionId, decisionItems) {
  const decisionRef = ref(database, `${sessionPath(uid, sessionId)}/decisionItems`);
  await set(decisionRef, {
    items: decisionItems,
    updatedAt: Date.now(),
  });
}

// --- Scope of Work ---

export async function saveScopeOfWork(uid, sessionId, scopeOfWork) {
  const scopeRef = ref(database, `${sessionPath(uid, sessionId)}/scopeOfWork`);
  await set(scopeRef, {
    ...scopeOfWork,
    updatedAt: Date.now(),
  });
}

// --- Scope of Work Photo Settings ---
// Stores user preferences for which space-analysis photos are included in
// the generated scope-of-work markdown. `excluded` holds URLs that should
// be hidden; any URL not listed is included by default.

export async function saveScopePhotoSettings(uid, sessionId, settings) {
  const photoRef = ref(database, `${sessionPath(uid, sessionId)}/scopePhotoSettings`);
  await set(photoRef, {
    excluded: Array.isArray(settings?.excluded) ? settings.excluded : [],
    updatedAt: Date.now(),
  });
}

// --- Mood Board ---

export async function saveMoodBoard(uid, sessionId, moodBoardItems, moodBoardGroups = []) {
  const moodBoardRef = ref(database, `${sessionPath(uid, sessionId)}/moodBoard`);
  await set(moodBoardRef, {
    items: stripUndefined(moodBoardItems),
    groups: stripUndefined(moodBoardGroups),
    updatedAt: Date.now(),
  });
}

// --- Floor Plan ---

export async function saveFloorPlan(uid, sessionId, floorPlanData) {
  const floorPlanRef = ref(database, `${sessionPath(uid, sessionId)}/floorPlan`);
  await set(floorPlanRef, {
    ...floorPlanData,
    updatedAt: Date.now(),
  });
}

// --- Quality Assurance Items ---

export async function saveQAItems(uid, sessionId, qaItems) {
  const qaRef = ref(database, `${sessionPath(uid, sessionId)}/qaItems`);
  await set(qaRef, {
    items: qaItems,
    updatedAt: Date.now(),
  });
}

// --- Unified Tasks ---

export async function saveTasks(uid, sessionId, tasks) {
  const tasksRef = ref(database, `${sessionPath(uid, sessionId)}/tasks`);
  await set(tasksRef, {
    items: stripUndefined(tasks),
    updatedAt: Date.now(),
  });
}

export async function loadSession(uid, sessionId) {
  const sessionRef = ref(database, sessionPath(uid, sessionId));
  const snapshot = await get(sessionRef);
  if (snapshot.exists()) {
    const data = snapshot.val();
    if (data.messages) {
      data.messages = Object.entries(data.messages)
        .map(([key, msg]) => ({ ...msg, _key: key }))
        .sort((a, b) => a.timestamp - b.timestamp);
    }
    // Ensure workItems is an array if it exists
    if (data.workItems && data.workItems.items) {
      data.workItems.items = Array.isArray(data.workItems.items)
        ? data.workItems.items
        : Object.values(data.workItems.items);
    }
    // Ensure spaceAnalysis is an array if it exists
    if (data.spaceAnalysis && data.spaceAnalysis.items) {
      data.spaceAnalysis.items = Array.isArray(data.spaceAnalysis.items)
        ? data.spaceAnalysis.items
        : Object.values(data.spaceAnalysis.items);
    }
    // Ensure decisionItems is an array if it exists
    if (data.decisionItems && data.decisionItems.items) {
      data.decisionItems.items = Array.isArray(data.decisionItems.items)
        ? data.decisionItems.items
        : Object.values(data.decisionItems.items);
    }
    // Ensure qaItems is an array if it exists
    if (data.qaItems && data.qaItems.items) {
      data.qaItems.items = Array.isArray(data.qaItems.items)
        ? data.qaItems.items
        : Object.values(data.qaItems.items);
    }
    // Ensure unified tasks collection is an array if it exists
    if (data.tasks && data.tasks.items) {
      data.tasks.items = Array.isArray(data.tasks.items)
        ? data.tasks.items
        : Object.values(data.tasks.items);
    }
    // Ensure custom documents collection is an array if it exists
    if (data.documents && data.documents.items) {
      data.documents.items = Array.isArray(data.documents.items)
        ? data.documents.items
        : Object.values(data.documents.items);
    }
    // Ensure moodBoard is an array if it exists
    if (data.moodBoard && data.moodBoard.items) {
      data.moodBoard.items = Array.isArray(data.moodBoard.items)
        ? data.moodBoard.items
        : Object.values(data.moodBoard.items);
    }
    if (data.moodBoard && data.moodBoard.groups) {
      data.moodBoard.groups = Array.isArray(data.moodBoard.groups)
        ? data.moodBoard.groups
        : Object.values(data.moodBoard.groups);
    }
    // Ensure floorPlan arrays are arrays if they exist
    if (data.floorPlan) {
      if (data.floorPlan.walls) {
        data.floorPlan.walls = Array.isArray(data.floorPlan.walls)
          ? data.floorPlan.walls
          : Object.values(data.floorPlan.walls);
      }
      if (data.floorPlan.openings) {
        data.floorPlan.openings = Array.isArray(data.floorPlan.openings)
          ? data.floorPlan.openings
          : Object.values(data.floorPlan.openings);
      }
      if (data.floorPlan.furniture) {
        data.floorPlan.furniture = Array.isArray(data.floorPlan.furniture)
          ? data.floorPlan.furniture
          : Object.values(data.floorPlan.furniture);
      }
      if (data.floorPlan.rooms) {
        data.floorPlan.rooms = Array.isArray(data.floorPlan.rooms)
          ? data.floorPlan.rooms
          : Object.values(data.floorPlan.rooms);
      }
    }
    return data;
  }
  return null;
}

export function subscribeToDocument(uid, sessionId, callback) {
  const docRef = ref(database, `${sessionPath(uid, sessionId)}/document`);
  return onValue(docRef, (snapshot) => {
    const data = snapshot.val();
    callback(data ? data.content : null);
  });
}

/**
 * Subscribe to real-time message updates for a session
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {function} callback - Callback with sorted messages array
 * @returns {function} - Unsubscribe function
 */
export function subscribeToMessages(uid, sessionId, callback) {
  const messagesRef = ref(database, `${sessionPath(uid, sessionId)}/messages`);
  return onValue(messagesRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const messages = Object.entries(data)
        .map(([key, msg]) => ({ ...msg, _key: key }))
        .sort((a, b) => a.timestamp - b.timestamp);
      callback(messages);
    } else {
      callback([]);
    }
  });
}

/**
 * Subscribe to real-time session state updates (work items, todos, etc.)
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {function} callback - Callback with session state object
 * @returns {function} - Unsubscribe function
 */
export function subscribeToSessionState(uid, sessionId, callback) {
  const sessionRef = ref(database, sessionPath(uid, sessionId));
  return onValue(sessionRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      // Normalize arrays
      const state = {
        todoList: data.todoList?.items || [],
        spaceContext: data.spaceContext?.data || {},
        projectEstimate: data.projectEstimate?.data || null,
        workItems: Array.isArray(data.workItems?.items)
          ? data.workItems.items
          : data.workItems?.items ? Object.values(data.workItems.items) : [],
        spaceAnalysis: Array.isArray(data.spaceAnalysis?.items)
          ? data.spaceAnalysis.items
          : data.spaceAnalysis?.items ? Object.values(data.spaceAnalysis.items) : [],
        decisionItems: Array.isArray(data.decisionItems?.items)
          ? data.decisionItems.items
          : data.decisionItems?.items ? Object.values(data.decisionItems.items) : [],
        scopeOfWork: data.scopeOfWork || null,
        scopePhotoSettings: {
          excluded: Array.isArray(data.scopePhotoSettings?.excluded)
            ? data.scopePhotoSettings.excluded
            : data.scopePhotoSettings?.excluded
              ? Object.values(data.scopePhotoSettings.excluded)
              : [],
        },
        qaItems: Array.isArray(data.qaItems?.items)
          ? data.qaItems.items
          : data.qaItems?.items ? Object.values(data.qaItems.items) : [],
        tasks: Array.isArray(data.tasks?.items)
          ? data.tasks.items
          : data.tasks?.items ? Object.values(data.tasks.items) : [],
        documents: Array.isArray(data.documents?.items)
          ? data.documents.items
          : data.documents?.items ? Object.values(data.documents.items) : [],
        moodBoard: Array.isArray(data.moodBoard?.items)
          ? data.moodBoard.items
          : data.moodBoard?.items ? Object.values(data.moodBoard.items) : [],
        moodBoardGroups: Array.isArray(data.moodBoard?.groups)
          ? data.moodBoard.groups
          : data.moodBoard?.groups ? Object.values(data.moodBoard.groups) : [],
        floorPlan: data.floorPlan || null,
      };
      callback(state);
    } else {
      callback({
        todoList: [],
        spaceContext: {},
        projectEstimate: null,
        workItems: [],
        spaceAnalysis: [],
        decisionItems: [],
        scopeOfWork: null,
        scopePhotoSettings: { excluded: [] },
        qaItems: [],
        tasks: [],
        documents: [],
        moodBoard: [],
        moodBoardGroups: [],
        floorPlan: null,
      });
    }
  });
}

export async function getSessions(uid) {
  const sessionsRef = ref(database, `users/${uid}/sessions`);
  const snapshot = await get(sessionsRef);
  if (snapshot.exists()) {
    const data = snapshot.val();
    return Object.entries(data)
      .map(([id, session]) => ({ ...session, id }))
      .filter((s) => s.createdAt)
      .sort((a, b) => b.createdAt - a.createdAt);
  }
  return [];
}

// --- Admin: cross-user session access ---
// Lets admin users open any session by URL (e.g. from an error report or
// Firebase console). Access is gated by the database security rules
// (admins/{uid} === true); non-admin reads will be denied at the RTDB layer.

/**
 * Load a session as an admin viewer. Skips the collaborator check that
 * `loadSharedSession` enforces — the database rules gate admin access.
 */
export async function loadSessionAsAdmin(ownerUid, sessionId) {
  const sessionRef = ref(database, `users/${ownerUid}/sessions/${sessionId}`);
  const snapshot = await get(sessionRef);
  if (!snapshot.exists()) return null;

  const data = snapshot.val();
  if (data.messages) {
    data.messages = Object.entries(data.messages)
      .map(([key, msg]) => ({ ...msg, _key: key }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }
  if (data.workItems && data.workItems.items) {
    data.workItems.items = Array.isArray(data.workItems.items)
      ? data.workItems.items
      : Object.values(data.workItems.items);
  }
  if (data.spaceAnalysis && data.spaceAnalysis.items) {
    data.spaceAnalysis.items = Array.isArray(data.spaceAnalysis.items)
      ? data.spaceAnalysis.items
      : Object.values(data.spaceAnalysis.items);
  }
  if (data.decisionItems && data.decisionItems.items) {
    data.decisionItems.items = Array.isArray(data.decisionItems.items)
      ? data.decisionItems.items
      : Object.values(data.decisionItems.items);
  }
  if (data.qaItems && data.qaItems.items) {
    data.qaItems.items = Array.isArray(data.qaItems.items)
      ? data.qaItems.items
      : Object.values(data.qaItems.items);
  }
  if (data.tasks && data.tasks.items) {
    data.tasks.items = Array.isArray(data.tasks.items)
      ? data.tasks.items
      : Object.values(data.tasks.items);
  }
  if (data.documents && data.documents.items) {
    data.documents.items = Array.isArray(data.documents.items)
      ? data.documents.items
      : Object.values(data.documents.items);
  }
  if (data.moodBoard && data.moodBoard.items) {
    data.moodBoard.items = Array.isArray(data.moodBoard.items)
      ? data.moodBoard.items
      : Object.values(data.moodBoard.items);
  }
  if (data.moodBoard && data.moodBoard.groups) {
    data.moodBoard.groups = Array.isArray(data.moodBoard.groups)
      ? data.moodBoard.groups
      : Object.values(data.moodBoard.groups);
  }

  data._isAdminView = true;
  data._ownerUid = ownerUid;
  data._role = 'admin';
  return data;
}

// --- Admin: cross-user listing ---

/**
 * Load a summary of every user keyed by uid: { sessionCount, sessions: [{id, title, createdAt}] }.
 * Admin-only (RTDB rule enforces). The full /users tree is read; this is
 * acceptable for the small admin tool but should not be used outside it.
 */
export async function getAllUsersAdmin() {
  const usersRef = ref(database, 'users');
  const snapshot = await get(usersRef);
  if (!snapshot.exists()) return [];
  const data = snapshot.val();
  return Object.entries(data).map(([uid, userData]) => {
    const sessionsObj = userData?.sessions || {};
    const sessions = Object.entries(sessionsObj)
      .map(([id, s]) => ({
        id,
        title: s?.title || null,
        createdAt: s?.createdAt || 0,
      }))
      .filter((s) => s.createdAt)
      .sort((a, b) => b.createdAt - a.createdAt);
    return {
      uid,
      sessionCount: sessions.length,
      sessions,
      lastActivity: sessions[0]?.createdAt || 0,
    };
  }).sort((a, b) => b.lastActivity - a.lastActivity);
}

/**
 * Load all error reports as an array sorted newest-first. Admin-only.
 */
export async function getAllErrorReportsAdmin() {
  const errorsRef = ref(database, 'errorReports');
  const snapshot = await get(errorsRef);
  if (!snapshot.exists()) return [];
  const data = snapshot.val();
  return Object.entries(data)
    .map(([id, report]) => ({ id, ...report }))
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

/**
 * Find the owner UID for a given session ID. Admin-only. Returns null if not
 * found. Reads each user's sessions list shallowly via the full /users tree.
 */
export async function findSessionOwnerAdmin(sessionId) {
  if (!sessionId) return null;
  const usersRef = ref(database, 'users');
  const snapshot = await get(usersRef);
  if (!snapshot.exists()) return null;
  const data = snapshot.val();
  for (const [uid, userData] of Object.entries(data)) {
    if (userData?.sessions && userData.sessions[sessionId]) {
      return uid;
    }
  }
  return null;
}

// --- Image Storage ---

/**
 * Upload an image to Firebase Storage
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {File} file - The image file to upload
 * @returns {Promise<{url: string, name: string, type: string}>} - Upload result with download URL
 */
export async function uploadImage(uid, sessionId, file) {
  // Bake any EXIF orientation into pixel data before upload so every
  // downstream consumer (chat, design board, PDF, Gemini) sees the same
  // pixels the user saw when picking the photo.
  const normalized = await bakeImageOrientation(file);
  const timestamp = Date.now();
  const safeName = normalized.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const path = `users/${uid}/sessions/${sessionId}/images/${timestamp}_${safeName}`;
  const imageRef = storageRef(storage, path);

  await uploadBytes(imageRef, normalized);
  const url = await getDownloadURL(imageRef);

  return {
    url,
    name: normalized.name,
    type: normalized.type,
    path,
  };
}

/**
 * Upload a file (image or PDF) to Firebase Storage
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {File} file - The file to upload
 * @returns {Promise<{url: string, name: string, type: string, path: string}>} - Upload result with download URL
 */
/**
 * Upload base64 image data to Firebase Storage
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {string} base64Data - Raw base64 image data (no data URL prefix)
 * @param {string} mimeType - MIME type (e.g., 'image/png')
 * @param {string} fileName - File name for storage
 * @returns {Promise<{url: string, name: string, type: string, path: string}>}
 */
export async function uploadBase64Image(uid, sessionId, base64Data, mimeType = 'image/png', fileName = 'generated.png') {
  const timestamp = Date.now();
  const safeName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  const path = `users/${uid}/sessions/${sessionId}/images/${timestamp}_${safeName}`;
  const imageRef = storageRef(storage, path);

  // Convert base64 to blob
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  await uploadBytes(imageRef, blob);
  const url = await getDownloadURL(imageRef);

  return { url, name: fileName, type: mimeType, path };
}

export async function uploadFile(uid, sessionId, file) {
  // Same EXIF baking as uploadImage when the file happens to be an image
  // (mood-board cards use uploadFile too). PDFs and other types pass through.
  const normalized = file?.type?.startsWith('image/')
    ? await bakeImageOrientation(file)
    : file;
  const timestamp = Date.now();
  const safeName = normalized.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const path = `users/${uid}/sessions/${sessionId}/files/${timestamp}_${safeName}`;
  const fileRef = storageRef(storage, path);

  await uploadBytes(fileRef, normalized);
  const url = await getDownloadURL(fileRef);

  return {
    url,
    name: normalized.name,
    type: normalized.type,
    path,
  };
}

// --- Attachments ---

/**
 * Save an attachment record to RTDB
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {object} attachment - Attachment record { kind, url, name, mimeType, label }
 * @returns {Promise<string>} The generated attachment ID
 */
export async function saveAttachment(uid, sessionId, attachment) {
  const attachmentsRef = ref(database, `${sessionPath(uid, sessionId)}/attachments`);
  const newRef = push(attachmentsRef);
  const id = newRef.key;
  await set(newRef, {
    ...attachment,
    createdAt: Date.now(),
  });
  return id;
}

/**
 * Subscribe to real-time attachment updates for a session
 * @param {string} uid - User ID
 * @param {string} sessionId - Session ID
 * @param {function} callback - Callback with attachments object (keyed by ID)
 * @returns {function} Unsubscribe function
 */
export function subscribeToAttachments(uid, sessionId, callback) {
  const attachmentsRef = ref(database, `${sessionPath(uid, sessionId)}/attachments`);
  return onValue(attachmentsRef, (snapshot) => {
    callback(snapshot.val() || {});
  });
}

// --- Project Collaborator Invites ---

/**
 * Create an invite for a collaborator
 * @param {string} ownerUid - Owner's user ID
 * @param {string} sessionId - Session ID to share
 * @param {string} sessionTitle - Session title for display
 * @param {string} email - Email of the person to invite
 * @param {string} role - Role: 'editor' or 'viewer'
 * @param {string} ownerEmail - Owner's email for display
 * @returns {Promise<object>} - The created invite
 */
export async function createInvite(ownerUid, sessionId, sessionTitle, email, role, ownerEmail) {
  // Check if invite already exists for this email and session
  const invitesRef = ref(database, 'invites');
  const snapshot = await get(invitesRef);

  if (snapshot.exists()) {
    const invites = snapshot.val();
    const existingInvite = Object.entries(invites).find(
      ([, inv]) => inv.email === email.toLowerCase() && inv.sessionId === sessionId && inv.ownerUid === ownerUid && inv.status === 'pending'
    );
    if (existingInvite) {
      throw new Error('An invite for this email already exists');
    }
  }

  const newInviteRef = push(invitesRef);
  const invite = {
    id: newInviteRef.key,
    ownerUid,
    sessionId,
    sessionTitle: sessionTitle || 'Untitled Project',
    email: email.toLowerCase(),
    ownerEmail,
    role,
    status: 'pending',
    createdAt: Date.now(),
  };
  await set(newInviteRef, invite);
  return invite;
}

/**
 * Get pending invites for a user by email
 * @param {string} email - User's email
 * @returns {Promise<array>} - Array of pending invites
 */
export async function getPendingInvites(email) {
  const invitesRef = ref(database, 'invites');
  const snapshot = await get(invitesRef);

  if (!snapshot.exists()) return [];

  const invites = snapshot.val();
  return Object.entries(invites)
    .filter(([, inv]) => inv.email === email.toLowerCase() && inv.status === 'pending')
    .map(([id, inv]) => ({ ...inv, id }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Get invites sent by a user for a specific session
 * @param {string} ownerUid - Owner's user ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<array>} - Array of invites for this session
 */
export async function getSessionInvites(ownerUid, sessionId) {
  const invitesRef = ref(database, 'invites');
  const snapshot = await get(invitesRef);

  if (!snapshot.exists()) return [];

  const invites = snapshot.val();
  return Object.entries(invites)
    .filter(([, inv]) => inv.ownerUid === ownerUid && inv.sessionId === sessionId)
    .map(([id, inv]) => ({ ...inv, id }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Accept an invite and become a collaborator
 * @param {string} inviteId - Invite ID to accept
 * @param {string} acceptingUid - UID of the user accepting
 * @param {string} acceptingEmail - Email of the user accepting
 */
export async function acceptInvite(inviteId, acceptingUid, acceptingEmail) {
  // Get the invite
  const inviteRef = ref(database, `invites/${inviteId}`);
  const snapshot = await get(inviteRef);

  if (!snapshot.exists()) {
    throw new Error('Invite not found');
  }

  const invite = snapshot.val();

  if (invite.status !== 'pending') {
    throw new Error('Invite is no longer valid');
  }

  // Update invite status
  await set(inviteRef, { ...invite, status: 'accepted', acceptedAt: Date.now() });

  // Add to session's collaborators list
  const collaboratorRef = ref(
    database,
    `users/${invite.ownerUid}/sessions/${invite.sessionId}/collaborators/${acceptingUid}`
  );
  await set(collaboratorRef, {
    uid: acceptingUid,
    email: acceptingEmail,
    role: invite.role,
    addedAt: Date.now(),
  });

  // Add to user's sharedWithMe list for easy lookup
  const sharedRef = ref(database, `users/${acceptingUid}/sharedWithMe/${invite.sessionId}`);
  await set(sharedRef, {
    ownerUid: invite.ownerUid,
    sessionId: invite.sessionId,
    sessionTitle: invite.sessionTitle,
    ownerEmail: invite.ownerEmail,
    role: invite.role,
    addedAt: Date.now(),
  });
}

/**
 * Decline an invite
 * @param {string} inviteId - Invite ID to decline
 */
export async function declineInvite(inviteId) {
  const inviteRef = ref(database, `invites/${inviteId}`);
  const snapshot = await get(inviteRef);

  if (!snapshot.exists()) {
    throw new Error('Invite not found');
  }

  const invite = snapshot.val();
  await set(inviteRef, { ...invite, status: 'declined', declinedAt: Date.now() });
}

/**
 * Cancel a pending invite (by owner)
 * @param {string} inviteId - Invite ID to cancel
 * @param {string} ownerUid - Owner's UID (for verification)
 */
export async function cancelInvite(inviteId, ownerUid) {
  const inviteRef = ref(database, `invites/${inviteId}`);
  const snapshot = await get(inviteRef);

  if (!snapshot.exists()) {
    throw new Error('Invite not found');
  }

  const invite = snapshot.val();

  if (invite.ownerUid !== ownerUid) {
    throw new Error('Not authorized to cancel this invite');
  }

  await remove(inviteRef);
}

/**
 * Get collaborators for a session
 * @param {string} ownerUid - Owner's user ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<array>} - Array of collaborators
 */
export async function getCollaborators(ownerUid, sessionId) {
  const collaboratorsRef = ref(database, `users/${ownerUid}/sessions/${sessionId}/collaborators`);
  const snapshot = await get(collaboratorsRef);

  if (!snapshot.exists()) return [];

  const collaborators = snapshot.val();
  return Object.entries(collaborators)
    .map(([uid, collab]) => ({ ...collab, uid }))
    .sort((a, b) => a.addedAt - b.addedAt);
}

/**
 * Remove a collaborator from a session
 * @param {string} ownerUid - Owner's user ID
 * @param {string} sessionId - Session ID
 * @param {string} collaboratorUid - Collaborator's UID to remove
 */
export async function removeCollaborator(ownerUid, sessionId, collaboratorUid) {
  // Remove from session's collaborators
  const collaboratorRef = ref(
    database,
    `users/${ownerUid}/sessions/${sessionId}/collaborators/${collaboratorUid}`
  );
  await remove(collaboratorRef);

  // Remove from user's sharedWithMe
  const sharedRef = ref(database, `users/${collaboratorUid}/sharedWithMe/${sessionId}`);
  await remove(sharedRef);
}

/**
 * Update a collaborator's role
 * @param {string} ownerUid - Owner's user ID
 * @param {string} sessionId - Session ID
 * @param {string} collaboratorUid - Collaborator's UID
 * @param {string} newRole - New role: 'editor' or 'viewer'
 */
export async function updateCollaboratorRole(ownerUid, sessionId, collaboratorUid, newRole) {
  const collaboratorRef = ref(
    database,
    `users/${ownerUid}/sessions/${sessionId}/collaborators/${collaboratorUid}`
  );
  const snapshot = await get(collaboratorRef);

  if (!snapshot.exists()) {
    throw new Error('Collaborator not found');
  }

  const collaborator = snapshot.val();
  await set(collaboratorRef, { ...collaborator, role: newRole });

  // Update in sharedWithMe too
  const sharedRef = ref(database, `users/${collaboratorUid}/sharedWithMe/${sessionId}`);
  const sharedSnapshot = await get(sharedRef);
  if (sharedSnapshot.exists()) {
    const shared = sharedSnapshot.val();
    await set(sharedRef, { ...shared, role: newRole });
  }
}

/**
 * Get sessions shared with a user
 * @param {string} uid - User's UID
 * @returns {Promise<array>} - Array of shared session references
 */
export async function getSharedSessions(uid) {
  const sharedRef = ref(database, `users/${uid}/sharedWithMe`);
  const snapshot = await get(sharedRef);

  if (!snapshot.exists()) return [];

  const shared = snapshot.val();
  return Object.entries(shared)
    .map(([sessionId, data]) => ({ ...data, sessionId }))
    .sort((a, b) => b.addedAt - a.addedAt);
}

/**
 * Load a shared session (for collaborators)
 * @param {string} ownerUid - Session owner's UID
 * @param {string} sessionId - Session ID
 * @param {string} requestingUid - UID of user requesting access
 * @returns {Promise<object|null>} - Session data or null
 */
export async function loadSharedSession(ownerUid, sessionId, requestingUid) {
  // First verify the user is a collaborator
  const collaboratorRef = ref(
    database,
    `users/${ownerUid}/sessions/${sessionId}/collaborators/${requestingUid}`
  );
  const collabSnapshot = await get(collaboratorRef);

  if (!collabSnapshot.exists()) {
    throw new Error('Not authorized to access this session');
  }

  const collaborator = collabSnapshot.val();

  // Load the session
  const sessionRef = ref(database, `users/${ownerUid}/sessions/${sessionId}`);
  const snapshot = await get(sessionRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.val();
  if (data.messages) {
    data.messages = Object.entries(data.messages)
      .map(([key, msg]) => ({ ...msg, _key: key }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }
  if (data.workItems && data.workItems.items) {
    data.workItems.items = Array.isArray(data.workItems.items)
      ? data.workItems.items
      : Object.values(data.workItems.items);
  }
  if (data.spaceAnalysis && data.spaceAnalysis.items) {
    data.spaceAnalysis.items = Array.isArray(data.spaceAnalysis.items)
      ? data.spaceAnalysis.items
      : Object.values(data.spaceAnalysis.items);
  }
  // Ensure decisionItems is an array if it exists
  if (data.decisionItems && data.decisionItems.items) {
    data.decisionItems.items = Array.isArray(data.decisionItems.items)
      ? data.decisionItems.items
      : Object.values(data.decisionItems.items);
  }
  // Ensure qaItems is an array if it exists
  if (data.qaItems && data.qaItems.items) {
    data.qaItems.items = Array.isArray(data.qaItems.items)
      ? data.qaItems.items
      : Object.values(data.qaItems.items);
  }
  // Ensure moodBoard is an array if it exists
  if (data.moodBoard && data.moodBoard.items) {
    data.moodBoard.items = Array.isArray(data.moodBoard.items)
      ? data.moodBoard.items
      : Object.values(data.moodBoard.items);
  }
  if (data.moodBoard && data.moodBoard.groups) {
    data.moodBoard.groups = Array.isArray(data.moodBoard.groups)
      ? data.moodBoard.groups
      : Object.values(data.moodBoard.groups);
  }

  // Add collaborator info to the session data
  data._isShared = true;
  data._ownerUid = ownerUid;
  data._role = collaborator.role;

  return data;
}

/**
 * Subscribe to document updates on a shared session
 * @param {string} ownerUid - Session owner's UID
 * @param {string} sessionId - Session ID
 * @param {function} callback - Callback for document updates
 * @returns {function} - Unsubscribe function
 */
export function subscribeToSharedDocument(ownerUid, sessionId, callback) {
  const docRef = ref(database, `users/${ownerUid}/sessions/${sessionId}/document`);
  return onValue(docRef, (snapshot) => {
    const data = snapshot.val();
    callback(data ? data.content : null);
  });
}

/**
 * Subscribe to real-time message updates on a shared session
 * @param {string} ownerUid - Session owner's UID
 * @param {string} sessionId - Session ID
 * @param {function} callback - Callback with sorted messages array
 * @returns {function} - Unsubscribe function
 */
export function subscribeToSharedMessages(ownerUid, sessionId, callback) {
  const messagesRef = ref(database, `users/${ownerUid}/sessions/${sessionId}/messages`);
  return onValue(messagesRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const messages = Object.entries(data)
        .map(([key, msg]) => ({ ...msg, _key: key }))
        .sort((a, b) => a.timestamp - b.timestamp);
      callback(messages);
    } else {
      callback([]);
    }
  });
}

/**
 * Subscribe to real-time session state on a shared session
 * @param {string} ownerUid - Session owner's UID
 * @param {string} sessionId - Session ID
 * @param {function} callback - Callback with session state object
 * @returns {function} - Unsubscribe function
 */
export function subscribeToSharedSessionState(ownerUid, sessionId, callback) {
  const sessionRef = ref(database, `users/${ownerUid}/sessions/${sessionId}`);
  return onValue(sessionRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const state = {
        todoList: data.todoList?.items || [],
        spaceContext: data.spaceContext?.data || {},
        projectEstimate: data.projectEstimate?.data || null,
        workItems: Array.isArray(data.workItems?.items)
          ? data.workItems.items
          : data.workItems?.items ? Object.values(data.workItems.items) : [],
        spaceAnalysis: Array.isArray(data.spaceAnalysis?.items)
          ? data.spaceAnalysis.items
          : data.spaceAnalysis?.items ? Object.values(data.spaceAnalysis.items) : [],
        decisionItems: Array.isArray(data.decisionItems?.items)
          ? data.decisionItems.items
          : data.decisionItems?.items ? Object.values(data.decisionItems.items) : [],
        scopeOfWork: data.scopeOfWork || null,
        scopePhotoSettings: {
          excluded: Array.isArray(data.scopePhotoSettings?.excluded)
            ? data.scopePhotoSettings.excluded
            : data.scopePhotoSettings?.excluded
              ? Object.values(data.scopePhotoSettings.excluded)
              : [],
        },
        qaItems: Array.isArray(data.qaItems?.items)
          ? data.qaItems.items
          : data.qaItems?.items ? Object.values(data.qaItems.items) : [],
        tasks: Array.isArray(data.tasks?.items)
          ? data.tasks.items
          : data.tasks?.items ? Object.values(data.tasks.items) : [],
        documents: Array.isArray(data.documents?.items)
          ? data.documents.items
          : data.documents?.items ? Object.values(data.documents.items) : [],
        moodBoard: Array.isArray(data.moodBoard?.items)
          ? data.moodBoard.items
          : data.moodBoard?.items ? Object.values(data.moodBoard.items) : [],
        moodBoardGroups: Array.isArray(data.moodBoard?.groups)
          ? data.moodBoard.groups
          : data.moodBoard?.groups ? Object.values(data.moodBoard.groups) : [],
        floorPlan: data.floorPlan || null,
      };
      callback(state);
    } else {
      callback({
        todoList: [],
        spaceContext: {},
        projectEstimate: null,
        workItems: [],
        spaceAnalysis: [],
        decisionItems: [],
        scopeOfWork: null,
        scopePhotoSettings: { excluded: [] },
        qaItems: [],
        tasks: [],
        documents: [],
        moodBoard: [],
        moodBoardGroups: [],
        floorPlan: null,
      });
    }
  });
}

/**
 * Leave a shared session (remove self as collaborator)
 * @param {string} ownerUid - Session owner's UID
 * @param {string} sessionId - Session ID
 * @param {string} leavingUid - UID of user leaving
 */
export async function leaveSharedSession(ownerUid, sessionId, leavingUid) {
  // Remove from session's collaborators
  const collaboratorRef = ref(
    database,
    `users/${ownerUid}/sessions/${sessionId}/collaborators/${leavingUid}`
  );
  await remove(collaboratorRef);

  // Remove from user's sharedWithMe
  const sharedRef = ref(database, `users/${leavingUid}/sharedWithMe/${sessionId}`);
  await remove(sharedRef);
}

/**
 * Delete all data for a user - projects, shared sessions, invites, everything
 * @param {string} uid - User's UID
 * @param {string} email - User's email (for cleaning up invites)
 */
export async function deleteAllUserData(uid, email) {
  // 1. Get all sessions to find collaborators to notify
  const sessionsRef = ref(database, `users/${uid}/sessions`);
  const sessionsSnapshot = await get(sessionsRef);

  if (sessionsSnapshot.exists()) {
    const sessions = sessionsSnapshot.val();

    // For each session, remove collaborators' sharedWithMe references
    for (const sessionId of Object.keys(sessions)) {
      const session = sessions[sessionId];
      if (session.collaborators) {
        for (const collaboratorUid of Object.keys(session.collaborators)) {
          const sharedRef = ref(database, `users/${collaboratorUid}/sharedWithMe/${sessionId}`);
          await remove(sharedRef);
        }
      }
    }
  }

  // 2. Leave all shared sessions (remove self from collaborators)
  const sharedWithMeRef = ref(database, `users/${uid}/sharedWithMe`);
  const sharedSnapshot = await get(sharedWithMeRef);

  if (sharedSnapshot.exists()) {
    const sharedSessions = sharedSnapshot.val();
    for (const sessionId of Object.keys(sharedSessions)) {
      const sharedSession = sharedSessions[sessionId];
      const collaboratorRef = ref(
        database,
        `users/${sharedSession.ownerUid}/sessions/${sessionId}/collaborators/${uid}`
      );
      await remove(collaboratorRef);
    }
  }

  // 3. Delete/update invites sent by this user and received by this user
  const invitesRef = ref(database, 'invites');
  const invitesSnapshot = await get(invitesRef);

  if (invitesSnapshot.exists()) {
    const invites = invitesSnapshot.val();
    for (const [inviteId, invite] of Object.entries(invites)) {
      // Delete invites sent by this user
      if (invite.ownerUid === uid) {
        await remove(ref(database, `invites/${inviteId}`));
      }
      // Delete pending invites sent to this user's email
      if (invite.email === email?.toLowerCase() && invite.status === 'pending') {
        await remove(ref(database, `invites/${inviteId}`));
      }
    }
  }

  // 4. Delete client-writable user data (sessions, sharedWithMe, favorites)
  // subscription and usage are server-managed (Cloud Functions only) and not
  // writable from the client — they are left for backend cleanup.
  await remove(ref(database, `users/${uid}/sessions`));
  await remove(ref(database, `users/${uid}/sharedWithMe`));
  await remove(ref(database, `users/${uid}/favorites`));

  // Note: Firebase Storage files (images) are not deleted here
  // In production, you'd want a Cloud Function to clean up storage and
  // server-managed data (subscription, usage)
}

// --- Favorites ---

/**
 * Toggle a session's favorite status
 * @param {string} uid - User's UID
 * @param {string} sessionId - Session ID to favorite/unfavorite
 * @param {boolean} isFavorited - Whether the session is currently favorited
 */
export async function toggleFavorite(uid, sessionId, isFavorited) {
  const favoriteRef = ref(database, `users/${uid}/favorites/${sessionId}`);
  if (isFavorited) {
    await remove(favoriteRef);
  } else {
    await set(favoriteRef, { addedAt: Date.now() });
  }
}

/**
 * Get all favorite session IDs for a user
 * @param {string} uid - User's UID
 * @returns {Promise<Set<string>>} - Set of favorited session IDs
 */
export async function getFavorites(uid) {
  const favoritesRef = ref(database, `users/${uid}/favorites`);
  const snapshot = await get(favoritesRef);
  if (!snapshot.exists()) return new Set();
  return new Set(Object.keys(snapshot.val()));
}

/**
 * Remove a session from favorites
 * @param {string} uid - User's UID
 * @param {string} sessionId - Session ID to remove
 */
export async function removeFavorite(uid, sessionId) {
  const favoriteRef = ref(database, `users/${uid}/favorites/${sessionId}`);
  await remove(favoriteRef);
}

// --- Error Reporting ---

export async function logErrorReport(uid, errorData) {
  const errorsRef = ref(database, 'errorReports');
  const newErrorRef = push(errorsRef);
  await set(newErrorRef, { ...errorData, uid });
}

// --- Feedback ---

export async function saveFeedback(uid, feedbackData) {
  const feedbackRef = ref(database, 'feedback');
  const newFeedbackRef = push(feedbackRef);
  await set(newFeedbackRef, {
    ...feedbackData,
    uid,
    createdAt: Date.now(),
    status: 'new',
  });
}

// --- Cloud Function Callables ---

const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
const fnSuffix = isDevMode ? 'Dev' : '';

const agentSendMessageFn = httpsCallable(functions, `agentSendMessage${fnSuffix}`, { timeout: 540000 });
const agentGenerateTitleFn = httpsCallable(functions, `agentGenerateTitle${fnSuffix}`);
const agentGetInitialMessageFn = httpsCallable(functions, `agentGetInitialMessage${fnSuffix}`);
const createCheckoutSessionFn = httpsCallable(functions, `createCheckoutSession${fnSuffix}`);
const createPortalSessionFn = httpsCallable(functions, `createPortalSession${fnSuffix}`);
const syncSubscriptionFn = httpsCallable(functions, `syncSubscription${fnSuffix}`);
const submitLeadFn = httpsCallable(functions, `submitLead${fnSuffix}`);
const cancelLeadHandoffFn = httpsCallable(functions, `cancelLeadHandoff${fnSuffix}`);

export async function callAgentSendMessage(sessionId, userMessage, attachmentIds = []) {
  const result = await agentSendMessageFn({ sessionId, userMessage, attachmentIds });
  return result.data;
}

export async function callAgentGenerateTitle(userMessage) {
  const result = await agentGenerateTitleFn({ userMessage });
  return result.data.title;
}

export async function callAgentGetInitialMessage() {
  const result = await agentGetInitialMessageFn();
  return result.data;
}

export async function callCreateCheckoutSession(priceId) {
  const result = await createCheckoutSessionFn({ priceId });
  return result.data;
}

export async function callCreatePortalSession() {
  const result = await createPortalSessionFn();
  return result.data;
}

export async function callSyncSubscription() {
  console.log('[syncSubscription] callSyncSubscription: invoking cloud function');
  const result = await syncSubscriptionFn();
  console.log('[syncSubscription] callSyncSubscription: result:', JSON.stringify(result.data));
  return result.data;
}

// Resolves a paused request_lead_handoff agent call. The server validates the
// payload, persists the lead under /users/{uid}/sessions/{sessionId}/lead,
// appends the functionResponse + a model acknowledgement to chatHistory, and
// clears pendingLeadHandoff. The caller does NOT need to re-invoke the agent.
export async function callSubmitLead(payload) {
  const result = await submitLeadFn(payload);
  return result.data;
}

export async function callCancelLeadHandoff(sessionId) {
  const result = await cancelLeadHandoffFn({ sessionId });
  return result.data;
}

// --- Subscription & Usage Real-time Listeners ---

export function subscribeToSubscription(uid, callback) {
  let syncAttempted = false;
  console.log('[syncSubscription] subscribeToSubscription: listening to users/' + uid + '/subscription');
  const subRef = ref(database, `users/${uid}/subscription`);
  const unsubscribe = onValue(subRef, (snapshot) => {
    const data = snapshot.val() || { tier: 'free' };
    console.log('[syncSubscription] subscribeToSubscription: Firebase data received:', JSON.stringify(data));
    callback(data);
    // Daily reconciliation: if a Stripe customer exists and we haven't synced
    // since the start of today (UTC), trigger a server-side sync. The server
    // re-checks lastSyncedAt and short-circuits, so repeat onValue fires don't
    // cause repeat Stripe calls. Catches webhook misses (e.g. stale paid tier
    // when the subscription was canceled in Stripe but the event was lost).
    if (!data.stripeCustomerId || syncAttempted) {
      return;
    }
    const now = new Date();
    const startOfTodayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const lastSyncedAt = data.lastSyncedAt || 0;
    const shouldSync = lastSyncedAt < startOfTodayUTC;
    console.log('[syncSubscription] subscribeToSubscription: sync check:', {
      stripeCustomerId: data.stripeCustomerId,
      tier: data.tier || null,
      lastSyncedAt,
      startOfTodayUTC,
      shouldSync,
    });
    if (shouldSync) {
      syncAttempted = true;
      console.log('[syncSubscription] subscribeToSubscription: triggering daily server-side sync');
      callSyncSubscription().catch((err) =>
        console.error('[syncSubscription] subscribeToSubscription: sync failed:', err)
      );
    }
  }, (error) => {
    console.error('[syncSubscription] subscribeToSubscription error:', error);
    // Fall back to free tier on error so the UI still renders
    callback({ tier: 'free' });
  });
  return unsubscribe;
}

export function subscribeToUsage(uid, callback) {
  const usageRef = ref(database, `users/${uid}/usage`);
  const unsubscribe = onValue(usageRef, (snapshot) => {
    callback(snapshot.val() || {});
  }, (error) => {
    console.error('subscribeToUsage error:', error);
    callback({});
  });
  return unsubscribe;
}

/**
 * Append a user message entry to chatHistory in RTDB so it appears immediately
 * via the real-time subscription, before the Cloud Function processes it.
 */
export async function appendUserChatEntry(uid, sessionId, entry) {
  const historyRef = ref(database, `${sessionPath(uid, sessionId)}/chatHistory`);
  const snapshot = await get(historyRef);
  const current = snapshot.val();
  const history = current ? (Array.isArray(current) ? current : Object.values(current)).filter(e => e != null) : [];
  await set(historyRef, [...history, entry]);
}

// --- New Subscriptions for Server-Side Agent ---

export function subscribeToChatHistory(uid, sessionId, callback) {
  const historyRef = ref(database, `${sessionPath(uid, sessionId)}/chatHistory`);
  const unsubscribe = onValue(historyRef, (snapshot) => {
    const history = snapshot.val();
    callback(history ? (Array.isArray(history) ? history : Object.values(history)).filter(e => e != null) : []);
  });
  return unsubscribe;
}

export function subscribeToStreamingResponse(uid, sessionId, callback) {
  const streamRef = ref(database, `${sessionPath(uid, sessionId)}/streamingResponse`);
  const unsubscribe = onValue(streamRef, (snapshot) => {
    callback(snapshot.val() || '');
  });
  return unsubscribe;
}

export function subscribeToAgentStatus(uid, sessionId, callback) {
  const statusRef = ref(database, `${sessionPath(uid, sessionId)}/agentStatus`);
  const unsubscribe = onValue(statusRef, (snapshot) => {
    callback(snapshot.val() || '');
  });
  return unsubscribe;
}

export function subscribeToAgentProcessing(uid, sessionId, callback) {
  const processingRef = ref(database, `${sessionPath(uid, sessionId)}/agentProcessing`);
  const unsubscribe = onValue(processingRef, (snapshot) => {
    callback(snapshot.val() || null);
  });
  return unsubscribe;
}

// Set / clear the durable processing flag from the client. The server overwrites
// this on entry to sendMessage and removes it on completion; the client writes
// it before invoking the callable so the loading state survives page refreshes
// and cold-start delay before the server takes over.
export async function setAgentProcessing(uid, sessionId, payload) {
  const processingRef = ref(database, `${sessionPath(uid, sessionId)}/agentProcessing`);
  await set(processingRef, payload);
}

export async function clearAgentProcessing(uid, sessionId) {
  const processingRef = ref(database, `${sessionPath(uid, sessionId)}/agentProcessing`);
  await remove(processingRef);
}

// --- Social Sharing ---

export async function uploadShareImage(uid, shareId, blob) {
  const path = `publicShares/${shareId}/composite.png`;
  const imageRef = storageRef(storage, path);
  await uploadBytes(imageRef, blob, { contentType: 'image/png' });
  const url = await getDownloadURL(imageRef);
  return url;
}

export async function createShareRecord(uid, sessionId, shareData) {
  const shareRef = push(ref(database, 'publicShares'));
  const shareId = shareRef.key;
  await set(shareRef, {
    uid,
    sessionId,
    imageUrl: shareData.imageUrl,
    sessionTitle: shareData.sessionTitle || null,
    designTitle: shareData.designTitle || null,
    roomType: shareData.roomType || null,
    createdAt: Date.now(),
  });
  return shareId;
}

export { auth, database, storage };
